import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  Box,
  Center,
  Container,
  Flex,
  Text,
  VStack,
  Spinner,
  Heading,
  Divider,
  Collapse,
} from '@chakra-ui/react';
import {QRCodeSVG} from 'qrcode.react';
import BackButton from '../../components/BackButton';
import {H2, H3, TextG10, TextG14, TextG16} from '../../components/typography';
import useAuth from '../../hooks/useAuth';
import {BlueButton} from '../../components/button';
import {ToastMessage} from '../../components/toast';
import dayjs from 'dayjs';
import ReferralsList, { MembershipReferral } from '../../components/ReferralsList';
import { fetchUserReferrals, fetchUserPromoCode } from '../../endpoints/api';
import { logShareEvent } from '../../utils/analytics';

interface ReferredUser {
  id: number;
  name: string;
  date: string;
}

const ReferAFriend = () => {
  const navigate = useNavigate();
  const {user} = useAuth();
  const [referredUsers, setReferredUsers] = useState<ReferredUser[]>([]);
  const [membershipReferrals, setMembershipReferrals] = useState<MembershipReferral[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMemberships, setIsLoadingMemberships] = useState(true);
  const [showMembershipDetails, setShowMembershipDetails] = useState(false);
  const [showFreeReferralDetails, setShowFreeReferralDetails] = useState(false);
  const [promoCode, setPromoCode] = useState<string>('');
  const [isLoadingPromoCode, setIsLoadingPromoCode] = useState(false);

  const userReferLink = location.origin + '/refer-a-friend?invite-code=' + user?.referralCode;
  const membershipReferLink = location.origin + '/gift?p=' + promoCode + '&invite-code=' + user?.referralCode;

  useEffect(() => {
    // Fetch referrals data from the API
    const fetchReferrals = async () => {
      try {
        const response = await fetchUserReferrals();
        
        if (response && response.data) {
          if (response.data.referred_users) {
            setReferredUsers(response.data.referred_users.map(user => ({
              id: user.id,
              name: user.name,
              date: user.redeemed_at,
            })));
          }
          
          if (response.data.membership_referrals) {
            setMembershipReferrals(response.data.membership_referrals);
          }
        }
      } catch (error) {
        console.error('Error fetching referrals:', error);
      } finally {
        setIsLoading(false);
        setIsLoadingMemberships(false);
      }
    };

    fetchReferrals();
    
    // Fetch promo code for membership referral
    const fetchPromoCode = async () => {
      setIsLoadingPromoCode(true);
      try {
        const response = await fetchUserPromoCode();
        if (response && response.data) {
          // Handle both existing and new promo code responses
          if (response.data.promo_code) {
            setPromoCode(response.data.promo_code);
          } else if (response.data.pc) {
            setPromoCode(response.data.pc);
          }
        }
      } catch (error) {
        console.error('Error fetching promo code:', error);
      } finally {
        setIsLoadingPromoCode(false);
      }
    };
    
    fetchPromoCode();
  }, []);

  const copyReferralLink = (link: string) => {
    navigator.clipboard.writeText(link);
    ToastMessage({
      status: 'success',
      text: 'Link copied to clipboard',
    });

    // Track the share event based on which link type is being shared
    const isMembershipLink = link.includes('/gift?p=');
    const contentType = isMembershipLink ? 'membership_referral_link' : 'referral_link';
    
    logShareEvent(contentType, 'copy_link', user?.referralCode || 'unknown'
    );
  };

  return (
    <Container h="fit-content" pb={6}>
      <Box pt={6}>
        <Flex alignItems="center">
          <BackButton pos="relative" onClick={() => navigate(-1)} sticky={false} />
          <Box flex="1">
            <Center ml="-35px">
              <H3>Refer a Friend</H3>
            </Center>
          </Box>
        </Flex>
      </Box>

      {/* Membership Referral Option */}
      {user?.hasEarlyAccess && (promoCode || isLoadingPromoCode) && (
        <Box p={5} borderRadius="lg" borderWidth="1px" my={6} bg="white">
          <H2 mt={0} mb={4}>
            Give a Month, Get a Month!
          </H2>
          
          <TextG14 mb={4}>
            <Text fontWeight="bold" mb={2}>Why refer friends to Local Insiders Club?</Text>
            <Text mb={2}>• You'll BOTH get a FREE month of membership</Text>
            <Text mb={2}>• Your friends will thank you for introducing them to exclusive local experiences</Text>
            <Text mb={2}>• Help build a stronger community of local explorers</Text>
            <Text>• The more friends you refer, the more free months you earn!</Text>
          </TextG14>

          <BlueButton 
            onClick={() => setShowMembershipDetails(!showMembershipDetails)} 
            width="100%" 
            mt={4}
            size="lg"
            isLoading={isLoadingPromoCode}
            isDisabled={isLoadingPromoCode || !promoCode}
          >
            {isLoadingPromoCode ? "Loading..." : showMembershipDetails ? "Hide Details" : "Refer to Local Insiders Club"}
          </BlueButton>

          {!isLoadingMemberships && membershipReferrals.length > 0 && (
            <TextG14 textAlign="center" mt={4} color="blue.500" fontWeight="medium">
              You've earned {membershipReferrals.length} free months so far!
            </TextG14>
          )}

          <Collapse 
            in={showMembershipDetails && !!promoCode} 
            animateOpacity 
            transition={{ enter: { duration: 0.5 }, exit: { duration: 0.3 } }}
            style={{ overflow: 'hidden' }}
          >
            <Box mt={6} pt={4} borderTop="1px solid" borderColor="gray.200">
              <TextG14 mb={4} color="gray.600">
                When friends join Walkabout and sign-up for Local Insiders Club with your invite, you both
                get a free month of membership!
              </TextG14>

              <Box>
                <TextG14 textAlign="center" fontWeight="medium" fontSize="md" mt={6}>
                  Have your friend scan your membership referral QR code
                </TextG14>
              </Box>

              <Flex justify="center" width="100%" my={4}>
                <QRCodeSVG value={membershipReferLink} size={180} />
              </Flex>

              <Box width="100%">
                <Box my={6}>
                  <TextG14 fontWeight="medium" textAlign="center" fontSize="md">
                    OR
                  </TextG14>
                </Box>

                <TextG14 textAlign="center" fontWeight="medium" fontSize="md" mb={2}>
                  Copy your membership referral link:
                </TextG14>
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  flexDirection="row"
                  pl="16px"
                  pr="4px"
                  py="4px"
                  border="1px solid #E2E8F0"
                  borderRadius="10px"
                  mb={4}>
                  <TextG10 mr="8px" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                    {membershipReferLink}
                  </TextG10>
                  <BlueButton
                    onClick={() => copyReferralLink(membershipReferLink)}
                    fontSize="10px"
                    maxH="40px"
                    borderRadius="10px">
                    Copy Link
                  </BlueButton>
                </Flex>
              </Box>

              <ReferralsList 
                referrals={membershipReferrals} 
                isLoading={isLoadingMemberships} 
                emptyMessage="You haven't invited anyone to join Local Insiders Club yet."
              />
            </Box>
          </Collapse>
        </Box>
      )}

      {/* Free App Referral Option */}
      <Box p={5} borderRadius="lg" borderWidth="1px" mt={6} bg="white">
        <H2 mt={0} mb={4}>
          Refer Friends to Walkabout
        </H2>

        <TextG14 mb={4}>
          <Text fontWeight="bold" mb={2}>Why refer friends to the free Walkabout app?</Text>
          <Text mb={2}>• Help friends discover local businesses and experiences</Text>
          <Text>• Build your local community of explorers</Text>
        </TextG14>

        <BlueButton 
          onClick={() => setShowFreeReferralDetails(!showFreeReferralDetails)} 
          width="100%" 
          mt={4}
          size="lg"
        >
          {showFreeReferralDetails ? "Hide Details" : "Refer to Free Walkabout App"}
        </BlueButton>


        <Collapse 
          in={showFreeReferralDetails} 
          animateOpacity 
          transition={{ enter: { duration: 0.5 }, exit: { duration: 0.3 } }}
          style={{ overflow: 'hidden' }}
        >
          <Box mt={6} pt={4} borderTop="1px solid" borderColor="gray.200">
            <VStack spacing={6}>
              <Box>
                <TextG14 textAlign="center" fontSize="md">
                  Have your friend scan your QR code
                </TextG14>
              </Box>

              <Flex justify="center" width="100%">
                <QRCodeSVG value={userReferLink} size={180} />
              </Flex>

              <Box>
                <TextG14 fontWeight="medium" textAlign="center" fontSize="md">
                  OR
                </TextG14>
              </Box>

              <Box width="100%">
                <TextG14 textAlign="center" fontWeight="medium" fontSize="md" mb={2}>
                  Copy your referral link and share it with friends!
                </TextG14>
                <Flex
                  alignItems="center"
                  flexDirection="row"
                  pl="16px"
                  pr="4px"
                  py="4px"
                  border="1px solid #E2E8F0"
                  borderRadius="10px">
                  <TextG10 mr="8px" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                    {userReferLink}
                  </TextG10>
                  <BlueButton
                    onClick={() => copyReferralLink(userReferLink)}
                    fontSize="10px"
                    maxH="40px"
                    borderRadius="10px">
                    Copy Link
                  </BlueButton>
                </Flex>
              </Box>
            </VStack>

            <Box mt={6}>
              <Heading as="h4" size="md" mb={4}>
                Friends You've Referred
              </Heading>
              <Divider mb={4} />

              {isLoading ? (
                <Center py={10}>
                  <Spinner size="md" />
                </Center>
              ) : referredUsers.length > 0 ? (
                <VStack spacing={4} align="stretch">
                  {referredUsers.map((referredUser) => (
                    <Flex
                      key={referredUser.id}
                      justifyContent="space-between"
                      p={3}
                      borderRadius="md"
                      boxShadow="sm"
                      border="1px solid #E2E8F0">
                      <Box>
                        <Text fontWeight="bold">{referredUser.name}</Text>
                        <Text fontSize="sm" color="gray.600">
                          Joined: {new Date(referredUser.date).toLocaleDateString()}
                        </Text>
                      </Box>
                    </Flex>
                  ))}
                </VStack>
              ) : (
                <Center py={10}>
                  <Text>You haven't referred any friends yet.</Text>
                </Center>
              )}
            </Box>
          </Box>
        </Collapse>
      </Box>
    </Container>
  );
};

export default ReferAFriend;
